import React from "react";
import { formatPdfExportDate } from "../../../helper/common";
import { renderTableWithCalculations } from "./exportTableCalculations";
import { mergeSmartChipsIntoFormData } from "../../Forms/utils/smartChipsUtils";
import { filterValidDropdownValues, findOptionLabelByValue } from "./TaskSidebarHeader.helper";
import { number, quote } from "../../../config";

/**
 * Formats array of objects into comma-separated display text
 * @param {Array} objectArray - Array of objects with label/value properties
 * @returns {string} Comma-separated string of display values
 * @author Bhavana
 */
const formatObjectArray = (objectArray) => {
  return objectArray
    .map((item) => {
      if (typeof item === "object" && item !== null) {
        return item.label || item.value || "";
      }
      return String(item);
    })
    .filter(Boolean)
    .join(", ");
};

/**
 * Formats array of values into comma-separated display text
 * @param {Array} valueArray - Array of primitive values (strings, numbers, etc.)
 * @returns {string} Comma-separated string of display values
 * @author Bhavana
 */
const formatValueArray = (valueArray) => {
  return valueArray
    .map((value) => String(value))
    .filter(Boolean)
    .join(", ");
};

/**
 * Formats array values into display text
 * @param {Array} valueArray - Array of values to process
 * @returns {string} Comma-separated display text
 * @author Bhavana
 */
const formatArrayValue = (valueArray) => {
  const validValues = filterValidDropdownValues(valueArray);
  
  if (validValues.length === 0) return "";
  
  const firstItem = validValues?.[0];
  const containsObjects = typeof firstItem === "object" && firstItem !== null && !Array.isArray(firstItem);
  
  if (containsObjects) {
    return formatObjectArray(validValues);
  } else {
    return formatValueArray(validValues);
  }
};

/**
 * Formats single object value into display text
 * @param {Object} valueObject - Object with label/value properties
 * @returns {string} Display text for the value
 * @author Bhavana
 */
const formatObjectValue = (valueObject) => {
  return valueObject?.label || valueObject?.value || "";
};

/**
 * Safely extract displayable value from form field
 * Handles both single values (object) and multi-select arrays
 * @param {*} fieldValue - The value from the form field (can be object, array, or primitive)
 * @returns {string} Display text for the field value
 * @author Bhavana
 */
const getDisplayValue = (fieldValue) => {
  if (fieldValue === null || fieldValue === undefined) return "";
  
  if (Array.isArray(fieldValue)) {
    return formatArrayValue(fieldValue);
  }
  
  if (typeof fieldValue === "object" && fieldValue !== null) {
    return formatObjectValue(fieldValue);
  }
  
  return String(fieldValue);
};

/**
 * Render HTML content safely for PDF export
 * @author Bhavana
 */
export const renderHtmlContent = (html) => {
  if (!html) return null;

  return (
    <div
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

/**
 * Render form element based on type for PDF export
 * @author Bhavana
 */
export const renderFormElement = (element, taskAttachments = [], isGridElement = false, user = null, submitDetails = null) => {
  const { type, id, properties = {}, value, name = "", smartChipId, isDeleted } = element;
  const { label = "", options = [], showChipName = true } = properties;
  const fieldLabel = showChipName ? (label || name || "") : "";

  if (properties?.isHidden && !isGridElement) {
    return null;
  }

  if (isDeleted) {
    return null;
  }

  if (properties?.isUserVariable || properties?.chipType === "user chip") {
    return renderUserChipElement(element, isGridElement, user, submitDetails);
  }

  switch (type) {
    case "text-label":
      return renderTextLabelElement(element, isGridElement);

    case "dropdown":
      const isDropdownUserChip = properties?.isUserVariable || properties?.chipType === "user chip";
      const isDropdownWorkflowVariable = properties?.isWorkflowVariable || !!smartChipId;
      if (isDropdownUserChip || (isDropdownWorkflowVariable && properties?.fixedVariableId)) {
        return renderUserChipElement(element, isGridElement, user, submitDetails);
      }
      return renderDropdownElement(element, fieldLabel, value, options, isGridElement);

    case "text":
    case "input":
      return renderInputElement(element, fieldLabel, value, isGridElement);

    case "date":
      return renderDateElement(element, fieldLabel, value, isGridElement);

    case "number":
      return renderNumberElement(element, fieldLabel, value, isGridElement);

    case "file-upload":
      return renderFileUploadElement(element, taskAttachments, isGridElement, user, submitDetails);

    case "horizontal-line":
      return <hr className="form-divider my-3" />;

    case "table":
      return renderTableWithCalculations(element);

    case "advanced-text":
      return renderAdvancedTextElement(element, isGridElement, user, submitDetails);

    case "row-container":
      return renderGridLayoutElement(element, taskAttachments, user, submitDetails);

    case "user-chip":
      return renderUserChipElement(element, isGridElement, user, submitDetails);

    default:
      return null;
  }
};

/**
 * Render text label element for PDF export
 * @author Bhavana
 */
const renderTextLabelElement = (element, isGridElement = false) => {
  const { properties = {} } = element;
  const textValue = properties?.value || "";
  
  if (!textValue || textValue.trim() === "") {
    return null;
  }

  const textClasses = [
    "form-label-title",
    isGridElement ? "mb-1" : "mb-2",
    properties?.bold && "font-weight-bold",
    properties?.italic && "font-italic",
    properties?.underline && "text-decoration-underline",
    properties?.textAlign === "center" && "text-center",
    properties?.textAlign === "right" && "text-right"
  ].filter(Boolean).join(" ");

  const textStyle = properties?.fontSize ? { fontSize: `${properties.fontSize}px` } : undefined;

  return (
    <div className="form-element text-label">
      <div className={textClasses} style={textStyle}>
        {textValue}
      </div>
    </div>
  );
};

/**
 * Render input/text element for PDF export
 * @author Bhavana
 */
const renderInputElement = (element, fieldLabel, value, isGridElement = false) => {
  const textDisplayValue = getDisplayValue(value);
  return (
    <div className={`form-element input-element ${isGridElement ? 'mb-2' : 'mb-3'}`}>
      {fieldLabel && <div className="form-field-label mb-1">{fieldLabel}</div>}
      <div className="form-field-value p-2 bg-light border rounded">
        {textDisplayValue || <span className="no-value-placeholder">No Value Selected</span>}
      </div>
    </div>
  );
};


/**
 * Gets labels from array of objects using dropdown options
 * @author Bhavana
 */
const getLabelsFromObjects = (objectArray, dropdownOptions = []) => {
  return objectArray
    .map((item) => {
      if (typeof item === "object" && item !== null && item.label) {
        return item.label;
      }
      
      if (typeof item === "object" && item !== null && item.value !== undefined) {
        return findOptionLabelByValue(item.value, dropdownOptions);
      }
      
      return findOptionLabelByValue(item, dropdownOptions);
    })
    .filter(Boolean);
};

/**
 * Gets labels from array of values using dropdown options
 * @author Bhavana
 */
const getLabelsFromArray = (valueArray, dropdownOptions = []) => {
  return valueArray
    .map((value) => findOptionLabelByValue(value, dropdownOptions))
    .filter(Boolean);
};

/**
 * Formats multi-select dropdown values into display text
 * @author Bhavana
 */
const formatMultiSelectValue = (valueArray, dropdownOptions = []) => {
  const validValues = filterValidDropdownValues(valueArray);
  
  if (validValues.length === 0) return "";
  
  const firstItem = validValues?.[0];
  const containsObjects = typeof firstItem === "object" && firstItem !== null && !Array.isArray(firstItem);
  
  if (containsObjects) {
    const labels = getLabelsFromObjects(validValues, dropdownOptions);
    return labels.join(", ");
  } else {
    const labels = getLabelsFromArray(validValues, dropdownOptions);
    return labels.join(", ");
  }
};

/**
 * Formats single-select dropdown object value into display text
 * @author Bhavana
 */
const formatSingleSelectObject = (valueObject, dropdownOptions = []) => {
  if (valueObject?.label) {
    return valueObject?.label;
  }
  
  if (valueObject?.value !== undefined) {
    return findOptionLabelByValue(valueObject?.value, dropdownOptions);
  }
  
  return "";
};

/**
 * Formats single-select dropdown value into display text
 * @author Bhavana
 */
const formatSingleSelectValue = (value, dropdownOptions = []) => {
  return findOptionLabelByValue(value, dropdownOptions);
};

/**
 * Gets the display value for a dropdown element
 * @author Bhavana
 */
const formatDropdownValue = (dropdownValue, isMultiSelect, dropdownOptions = []) => {
  if (dropdownValue === null || dropdownValue === undefined || dropdownValue === "") {
    return "";
  }
  
  if (Array.isArray(dropdownValue)) {
    return formatMultiSelectValue(dropdownValue, dropdownOptions);
  }
  
  if (typeof dropdownValue === "object" && !Array.isArray(dropdownValue)) {
    return formatSingleSelectObject(dropdownValue, dropdownOptions);
  }
  
  return formatSingleSelectValue(dropdownValue, dropdownOptions);
};

/**
 * Render dropdown element for PDF export
 * Handles both single-select (object) and multi-select (array) dropdowns
 * @author Bhavana
 */
const renderDropdownElement = (element, fieldLabel, value, options = [], isGridElement = false) => {
  const { properties = {} } = element;
  const { multiSelect = false } = properties;
  
  const dropdownDisplayValue = formatDropdownValue(value, multiSelect, options);
  
  return (
    <div className={`form-element dropdown-element ${isGridElement ? 'mb-2' : 'mb-3'}`}>
      {fieldLabel && <div className="form-field-label mb-1">{fieldLabel}</div>}
      <div className="form-field-value p-2 bg-light border rounded">
        {dropdownDisplayValue || <span className="no-value-placeholder">No Value Selected</span>}
      </div>
    </div>
  );
};

/**
 * Render date element for PDF export
 * @author Bhavana
 */
const renderDateElement = (element, fieldLabel, value, isGridElement = false) => {
  return (
    <div className={`form-element date-element ${isGridElement ? 'mb-2' : 'mb-3'}`}>
      {fieldLabel && <div className="form-field-label mb-1">{fieldLabel}</div>}
      <div className="form-field-value p-2 bg-light border rounded">
        {value ? formatPdfExportDate(value) : <span className="no-value-placeholder">No Value Selected</span>}
      </div>
    </div>
  );
};

/**
 * Render number element for PDF export
 * @author Bhavana
 */
const renderNumberElement = (element, fieldLabel, value, isGridElement = false) => {
  const numberDisplayValue = getDisplayValue(value);
  return (
    <div className={`form-element number-element ${isGridElement ? 'mb-2' : 'mb-3'}`}>
      {fieldLabel && <div className="form-field-label mb-1">{fieldLabel}</div>}
      <div className="form-field-value p-2 bg-light border rounded">
        {numberDisplayValue || <span className="no-value-placeholder">No Value Selected</span>}
      </div>
    </div>
  );
};

/**
 * Render advanced text element with rich text/HTML content for PDF export
 * @author Bhavana
 */
const renderAdvancedTextElement = (element, isGridElement = false, user = null, submitDetails = null) => {
  const { properties = {} } = element;
  const {
    value = "",
    fontSize = 14,
    bold = false,
    italic = false,
    underline = false,
    textAlign = "left"
  } = properties;

  if (!value || value.trim() === "") {
    return null;
  }

  const hasHtmlContent = /<[^>]+>/.test(value);

  const contentClasses = [
    "adv-text-content",
    bold && "font-weight-bold",
    italic && "font-italic",
    underline && "text-decoration-underline",
    textAlign === "center" && "text-center",
    textAlign === "right" && "text-right",
    isGridElement ? "mb-1" : "mb-2"
  ].filter(Boolean).join(" ");

  const contentStyle = { fontSize: `${fontSize}px` };

  return (
    <div className={`form-element advanced-text-element ${isGridElement ? 'mb-2' : 'mb-3'}`}>
      {hasHtmlContent ? (
        <div
          className={contentClasses}
          style={contentStyle}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ) : (
        <div className={contentClasses} style={contentStyle}>
          {value}
        </div>
      )}
    </div>
  );
};

/**
 * Render user chip element for user variables like employee info
 * @author Bhavana
 */
const renderUserChipElement = (element, isGridElement = false, user = null, submitDetails = null) => {
  const { properties = {}, value, name = "" } = element;
  const { label = "", fixedVariableId, showChipName = true } = properties;

  // Only show label if showChipName is true (for user chips)
  const fieldLabel = showChipName ? (label || name || "") : "";

  let displayValue = "";

  if (typeof value === 'object' && value !== null && value.label) {
    displayValue = value.label;
  } else if (value && typeof value === 'string') {
    displayValue = value;
  }
  else if (fixedVariableId && user) {
    if (fixedVariableId === 10) {
      displayValue = submitDetails?.submitterName || user?.Name || "Unknown User";
    } else if (fixedVariableId === 11) {
      displayValue = user?.UserId || "";
    } else if (fixedVariableId === 12) {
      displayValue = user?.MobileNo || "";
    } else if (fixedVariableId === 13) {
      displayValue = user?.email || "";
    } else if (fixedVariableId === 14) {
      displayValue = user?.userDomainEmail || "";
    } else if (fixedVariableId === 3) {
      displayValue = user?.Department || "";
    } else if (fixedVariableId === 6) {
      displayValue = user?.JobRole || "";
    } else if (fixedVariableId === 7) {
      displayValue = user?.Manager || "";
    } else if (fixedVariableId === 8) {
      displayValue = user?.OfficeLocation || "";
    }
  }

  if (!displayValue && properties?.value) {
    displayValue = typeof properties.value === 'object'
      ? properties.value.label || properties.value.value || ""
      : properties.value;
  }

  displayValue = displayValue ? String(displayValue) : "";

  return (
    <div className={`form-element user-chip-element ${isGridElement ? 'mb-2' : 'mb-3'}`}>
      {fieldLabel && <div className="form-field-label mb-1">{fieldLabel}</div>}
      <div className="form-field-value p-2 bg-light border rounded">
        {displayValue || <span className="no-value-placeholder">No Value Selected</span>}
      </div>
    </div>
  );
};

/**
 * Render grid layout element (row-container) for PDF export
 * Matches GridLayoutRenderer.jsx logic exactly
 * @author Bhavana
 */
const renderGridLayoutElement = (element, taskAttachments = [], user = null, submitDetails = null) => {
  const { properties = {}, gridElements = [] } = element;
  const {
    rowCount = 1,
    columnWidths = [50, 50],
    gap = 8
  } = properties;

  // Ensure we have valid grid structure (matches GridLayoutRenderer)
  if (!gridElements || gridElements.length === 0) {
    return null;
  }

  const gridTemplateColumns = columnWidths.map((width) => `${width}%`).join(" ");

  const renderGridCell = (cellElement, rowIndex, colIndex) => {
    if (!cellElement) {
      return <div key={`${rowIndex}-${colIndex}`} className="grid-cell-empty" />;
    }

    if (cellElement.properties?.isHidden) {
      return <div key={`${rowIndex}-${colIndex}`} className="grid-cell-empty" />;
    }

    if (cellElement?.isDeleted) {
      return <div key={`${rowIndex}-${colIndex}`} className="grid-cell-empty" />;
    }

    return (
      <div key={`cell-${rowIndex}-${colIndex}`} className="grid-cell">
        {renderFormElement(cellElement, taskAttachments, true, user, submitDetails)}
      </div>
    );
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns,
    gridTemplateRows: `repeat(${rowCount}, auto)`,
    gap: `${gap}px`
  };

  return (
    <div className="form-element grid-layout-element mb-3">
      <div className="grid-container" style={gridStyle}>
        {gridElements.map((row, rowIndex) =>
          row.map((cell, colIndex) => (
            <div key={`cell-${rowIndex}-${colIndex}`} className="grid-cell align-items-center">
              {renderGridCell(cell, rowIndex, colIndex)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Render file upload element with attachments for PDF export
 * @author Bhavana
 */
const renderFileUploadElement = (element, taskAttachments, isGridElement = false, user = null, submitDetails = null) => {
  const { value, properties = {}, name = "" } = element;
  const { label = "", showChipName = true } = properties;

  const fieldLabel = showChipName ? (label || name || "") : "";

  /** Format file size in human-readable format
   * @author Bhavana */
  const formatFileSize = (size) => {
    if (!size) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  let files = [];

  if (Array.isArray(value)) {
    files = value;
  }
  else if (value && typeof value === 'object') {
    files = [value];
  }
  else if (value && typeof value === 'string') {
    files = [{ name: value, fileName: value }];
  }

  if (files.length === 0 && taskAttachments.length > 0) {
    files = taskAttachments.map(attachment => ({
      AttachmentName: attachment.AttachmentName,
      FileSize: attachment.FileSize,
      name: attachment.AttachmentName,
      size: attachment.FileSize
    }));
  }

  return (
    <div className={`form-element file-element ${isGridElement ? 'mb-2' : 'mb-3'}`}>
      {fieldLabel && <div className="form-field-label mb-1">{fieldLabel}</div>}
      <div className="form-field-value p-2 bg-light border rounded">
        {files.length > 0 ? (
          <div className="uploaded-files-list">
            {files.map((file, index) => {
              const fileName = file?.AttachmentName || file?.name || file?.fileName || `File ${index + 1}`;
              const fileSize = file?.FileSize || file?.size;
              const formattedSize = fileSize ? formatFileSize(fileSize) : "";

              return (
                <div key={index} className="uploaded-file-item d-flex align-items-center mb-1">
                  <span className="file-name flex-grow-1">{fileName}</span>
                  {formattedSize && <span className="file-size ml-2">{formattedSize}</span>}
                </div>
              );
            })}
          </div>
        ) : (
          <span className="no-files-text">No files uploaded</span>
        )}
      </div>
    </div>
  );
};


/**
 * Render form subsection with visible elements for PDF export
 * @author Bhavana
 */
export const renderFormSubsection = (subsection, taskAttachments = [], user = null, submitDetails = null) => {
  const { id, title, elements = [], color } = subsection;

  const visibleElements = elements.filter((element) => {
    if (element?.isDeleted) {
      return false;
    }

    if (element.properties?.isHidden) {
      return false;
    }

    // For text-label elements, check if they have content
    if (element.type === "text-label") {
      const value = element.properties?.value || "";
      return value && value.trim() !== "";
    }

    // Other element types always render
    return true;
  });

  // Don't render subsection if no elements will be visible (matches FormRenderer)
  if (visibleElements.length === 0) {
    return null;
  }

  return (
    <div className="form-subsection mb-4">
      {title && <h3 className="form-subsection-title mb-2 pb-1">{title}</h3>}
      <div className="form-elements">
        {visibleElements.map((element, index) => (
          <div key={element.id || index} className="form-element-wrapper mb-3">
            {renderFormElement(element, taskAttachments, false, user, submitDetails)}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Render complete form with all subsections for PDF export
 * @author Bhavana
 */
export const renderForms = (formData, taskAttachments = [], user = null, task = null) => {
  if (!formData) {
    return (
      <div className="forms-content">
        <p>{quote.NO_FORM_DATA_AVAILABLE}</p>
      </div>
    );
  }

  let content = (formData?.subsections && Array.isArray(formData?.subsections)) ? formData : formData?.content ?? formData;
  if (!content || !content?.subsections || !Array.isArray(content?.subsections) || content?.subsections?.length === number.ZERO) {
    return (
      <div className="forms-content">
        <p>{quote.NO_FORM_DATA_AVAILABLE}</p>
      </div>
    );
  }

  const smartChips = content?.smartChips || formData.smartChips || {};

  // Merge smartChips into form data if they exist (new structure)
  if (smartChips && Object.keys(smartChips).length > 0) {
    content = mergeSmartChipsIntoFormData(content, smartChips);
  }

  const subsections = content.subsections;

  const submitDetails = {
    submitterName: task?.CreatedByName || user?.Name,
    submitterId: task?.CreatedBy || user?.id
  };

  const renderedSubsections = subsections?.map((subsection, index) => ({
    key: subsection?.id || index,
    content: renderFormSubsection(subsection, taskAttachments, user, submitDetails)
  }));

  const validSubsections = renderedSubsections?.filter(item => item?.content);

  if (validSubsections?.length === number.ZERO) {
    return (
      <div className="forms-content">
        <p>{quote.NO_FORM_DATA_AVAILABLE}</p>
      </div>
    );
  }

  return (
    <div className="forms-content">
      <div className="form-body">
        {validSubsections?.map((item) => (
          <div key={item?.key}>
            {item?.content}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Check if review section has last updated meta
 * @param {Object} formData - Form data
 * @param {Array} assigneeList - Assignee list
 * @returns {boolean} True if review section has last updated meta, false otherwise
 * @author Bhavana
 */
const hasReviewSectionLastUpdatedMeta = (formData, assigneeList) => {
  if (!formData?.reviewLastUpdatedAt) return false;
  const byLabel = assigneeList?.find((a) => String(a?.value) === String(formData?.reviewLastUpdatedBy))?.label;
  return Boolean(byLabel);
};

/**
 * Render review section for PDF export
 * @param {Array|null|undefined} assigneeList - Required to detect whether review was saved (LAST_UPDATED_BY).
 * @author Bhavana
 */
export const renderReviewSection = (formData, taskAttachments = [], user = null, task = null, assigneeList) => {
  if (!formData) {
    return (
      <div className="forms-content">
        <p>{quote.NO_FORM_DATA_AVAILABLE}</p>
      </div>
    );
  }
  if (!hasReviewSectionLastUpdatedMeta(formData, assigneeList)) {
    return (
      <div className="forms-content">
        <p>{quote.NO_FORM_DATA_AVAILABLE}</p>
      </div>
    );
  }
  let content = formData?.hiddenSubsections && Array.isArray(formData?.hiddenSubsections) ? formData : formData?.content ?? formData;
  if (!content || !content?.hiddenSubsections || !Array.isArray(content?.hiddenSubsections) || content?.hiddenSubsections?.length === number.ZERO) {
    return (
      <div className="forms-content">
        <p>{quote.NO_FORM_DATA_AVAILABLE}</p>
      </div>
    );
  }
  const smartChips = content?.smartChips || formData.smartChips || {};
  if (smartChips && Object.keys(smartChips).length > number.ZERO) {
    content = mergeSmartChipsIntoFormData(content, smartChips);
  }
  const hiddenSubsections = content.hiddenSubsections;
  const submitDetails = {
    submitterName: task?.CreatedByName || user?.Name,
    submitterId: task?.CreatedBy || user?.id
  };
  const renderedSubsections = hiddenSubsections?.map((subsection, index) => ({
    key: subsection?.id || `hidden-${index}`,
    content: renderFormSubsection(subsection, taskAttachments, user, submitDetails)
  }));

  const validSubsections = renderedSubsections?.filter((item) => item?.content);

  if (validSubsections?.length === number.ZERO) {
    return (
      <div className="forms-content">
        <p>{quote.NO_FORM_DATA_AVAILABLE}</p>
      </div>
    );
  }

  return (
    <div className="forms-content review-section-export">
      <div className="form-body">
        {validSubsections?.map((item) => (
          <div key={item?.key}>
            {item?.content}
          </div>
        ))}
      </div>
    </div>
  );
};
